import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import {
  AdminLayout,
  AdminDashboard,
  AdminPosts,
  AdminJobs,
  AdminCompanies,
  AdminTargets,
  AdminScraper,
} from './pages/admin';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Hook to sync user with backend and get user data
function useBackendUser() {
  const { isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [backendUser, setBackendUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function syncWithBackend() {
      if (!isLoaded) return;

      if (!isSignedIn) {
        setBackendUser(null);
        setLoading(false);
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const user = await response.json();
          setBackendUser(user);
        }
      } catch (error) {
        console.error('Failed to sync user with backend:', error);
      } finally {
        setLoading(false);
      }
    }

    syncWithBackend();
  }, [isSignedIn, isLoaded, getToken]);

  return { backendUser, loading, isSignedIn, isLoaded };
}

// 404 Page
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-cream-100 mb-4">404</h1>
        <p className="text-cream-200/70">Page not found</p>
      </div>
    </div>
  );
}

// Protected route wrapper for authenticated users
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-cream-200/50">Loading...</div>
      </div>
    );
  }

  // Redirect to landing if not signed in
  if (!isSignedIn) {
    window.location.href = '/';
    return null;
  }

  return <>{children}</>;
}

// Protected route wrapper for admin
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { backendUser, loading, isSignedIn, isLoaded } = useBackendUser();

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-cream-200/50">Loading...</div>
      </div>
    );
  }

  // Show 404 if not signed in or not admin/moderator
  if (!isSignedIn || !backendUser || !['ADMIN', 'MODERATOR'].includes(backendUser.role)) {
    return <NotFound />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Router>
      <div className="min-h-screen text-cream-100 font-body antialiased">
        {/* Texture overlay */}
        <div className="fixed inset-0 texture-overlay pointer-events-none"></div>

        <Routes>
          <Route path="/" element={<LandingPage />} />

          {/* User Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="posts" element={<AdminPosts />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="targets" element={<AdminTargets />} />
            <Route path="scraper" element={<AdminScraper />} />
          </Route>

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
